import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", "progressBar", "indicator", "section",
    "customerPresentInput", "customerPresentBtn", "customerAbsentBtn",
    "repairStatusInput", "repairStatusBtn",
    "newBikeSection", "newBikeInput",
    "happyCustomerBtn", "happyCustomerInput",
    "signatureSection", "signatureCanvas", "signatureInput",
    "commentsInput",
    "submitBtn", "submitBtnText", "submitBtnLoader",
    "validationMessage"
  ]

  static values = {
    ticketId: String
  }

  connect() {
    // Estado del formulario
    this.formData = {
      customerPresent: null,
      repairStatus: null,
      newBikeNumber: null,
      comments: null,
      signature: null
    }
    
    // Canvas se inicializa lazy cuando se hace visible
    this.canvasInitialized = false
  }

  // Llamado cuando el formulario se hace visible
  initializeLazy() {
    if (this.canvasInitialized) return
    this.canvasInitialized = true
    this.initializeSignatureCanvas()
    this.updateProgress()
  }

  initializeSignatureCanvas() {
    if (!this.hasSignatureCanvasTarget) return
    
    const canvas = this.signatureCanvasTarget
    const ctx = canvas.getContext('2d')
    
    // Variables para tracking del dibujo
    this.isDrawing = false
    this.hasSignature = false
    
    // Configurar canvas
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    // Event listeners para mouse
    canvas.addEventListener('mousedown', this.startDrawing.bind(this))
    canvas.addEventListener('mousemove', this.draw.bind(this))
    canvas.addEventListener('mouseup', this.stopDrawing.bind(this))
    canvas.addEventListener('mouseout', this.stopDrawing.bind(this))
    
    // Event listeners para touch
    canvas.addEventListener('touchstart', this.startDrawing.bind(this))
    canvas.addEventListener('touchmove', this.draw.bind(this))
    canvas.addEventListener('touchend', this.stopDrawing.bind(this))
  }

  startDrawing(e) {
    e.preventDefault()
    this.isDrawing = true
    const canvas = this.signatureCanvasTarget
    const ctx = canvas.getContext('2d')
    const coords = this.getCoordinates(e)
    
    ctx.beginPath()
    ctx.moveTo(coords.x, coords.y)
  }

  draw(e) {
    if (!this.isDrawing) return
    e.preventDefault()
    
    const canvas = this.signatureCanvasTarget
    const ctx = canvas.getContext('2d')
    const coords = this.getCoordinates(e)
    
    ctx.lineTo(coords.x, coords.y)
    ctx.stroke()
    
    this.hasSignature = true
    this.updateProgress()
  }

  stopDrawing(e) {
    if (!this.isDrawing) return
    e.preventDefault()
    this.isDrawing = false
  }

  getCoordinates(e) {
    const canvas = this.signatureCanvasTarget
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    let clientX, clientY
    
    if (e.type.startsWith('touch')) {
      const touch = e.touches[0] || e.changedTouches[0]
      clientX = touch.clientX
      clientY = touch.clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    }
  }

  clearSignature() {
    const canvas = this.signatureCanvasTarget
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.hasSignature = false
    this.updateProgress()
  }

  selectCustomerPresent(e) {
    const value = e.currentTarget.dataset.value === 'true'
    this.formData.customerPresent = value
    this.customerPresentInputTarget.value = value
    
    // Actualizar UI de botones
    this.customerPresentBtnTargets.forEach(btn => {
      if (btn.dataset.value === e.currentTarget.dataset.value) {
        btn.classList.add('selected', 'border-orange-500', 'bg-orange-50')
      } else {
        btn.classList.remove('selected', 'border-orange-500', 'bg-orange-50')
      }
    })
    
    // Mostrar/ocultar sección de firma
    if (value && this.hasSignatureSectionTarget) {
      this.signatureSectionTarget.classList.remove('hidden')
    } else if (this.hasSignatureSectionTarget) {
      this.signatureSectionTarget.classList.add('hidden')
      this.clearSignature()
    }
    
    this.updateProgress()
  }

  selectRepairStatus(e) {
    const value = e.currentTarget.dataset.value
    this.formData.repairStatus = value
    this.repairStatusInputTarget.value = value
    
    // Actualizar UI de botones
    this.repairStatusBtnTargets.forEach(btn => {
      if (btn.dataset.value === value) {
        btn.classList.add('selected', 'border-orange-500', 'bg-orange-50')
      } else {
        btn.classList.remove('selected', 'border-orange-500', 'bg-orange-50')
      }
    })
    
    // Mostrar/ocultar sección de nuevo número de bici
    if (value === 'cambio_bici' && this.hasNewBikeSectionTarget) {
      this.newBikeSectionTarget.classList.remove('hidden')
      this.newBikeInputTarget.focus()
    } else if (this.hasNewBikeSectionTarget) {
      this.newBikeSectionTarget.classList.add('hidden')
      this.newBikeInputTarget.value = ''
    }
    
    this.updateProgress()
  }

  selectHappyCustomer(e) {
    const value = e.currentTarget.dataset.value
    this.happyCustomerInputTarget.value = value
    
    // Actualizar UI de botones
    this.happyCustomerBtnTargets.forEach(btn => {
      if (btn.dataset.value === value) {
        btn.classList.add('selected', 'border-orange-500', 'bg-orange-50')
      } else {
        btn.classList.remove('selected', 'border-orange-500', 'bg-orange-50')
      }
    })
  }

  updateProgress() {
    // Calcular progreso
    let completed = 0
    let total = 2 // customer_present y repair_status son obligatorios
    
    if (this.formData.customerPresent !== null) completed++
    if (this.formData.repairStatus !== null) completed++
    
    // Si cambio de bici, el número es obligatorio
    if (this.formData.repairStatus === 'cambio_bici') {
      total++
      if (this.hasNewBikeInputTarget && this.newBikeInputTarget.value.trim()) {
        completed++
      }
    }
    
    // Si cliente presente, firma es obligatoria
    if (this.formData.customerPresent === true) {
      total++
      if (this.hasSignature) completed++
    }
    
    // Comentarios son opcionales, no cuentan para progreso obligatorio
    // pero los trackéamos
    if (this.hasCommentsInputTarget && this.commentsInputTarget.value.trim()) {
      this.formData.comments = this.commentsInputTarget.value
    }
    
    const percentage = Math.round((completed / total) * 100)
    
    // Actualizar barra de progreso
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
    }
    
    // Actualizar indicadores de sección
    this.updateSectionIndicators()
    
    // Habilitar/deshabilitar botón de submit
    const isValid = completed === total
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = !isValid
    }
    
    // Mensaje de validación
    if (this.hasValidationMessageTarget) {
      if (!isValid) {
        const missing = []
        if (this.formData.customerPresent === null) missing.push("presencia del cliente")
        if (this.formData.repairStatus === null) missing.push("estado de reparación")
        if (this.formData.repairStatus === 'cambio_bici' && !this.newBikeInputTarget.value.trim()) {
          missing.push("número de nueva bici")
        }
        if (this.formData.customerPresent === true && !this.hasSignature) {
          missing.push("firma del cliente")
        }
        
        this.validationMessageTarget.textContent = `Falta: ${missing.join(', ')}`
        this.validationMessageTarget.classList.add('text-orange-600')
      } else {
        this.validationMessageTarget.textContent = "✓ Todo listo para guardar"
        this.validationMessageTarget.classList.remove('text-orange-600')
        this.validationMessageTarget.classList.add('text-green-600')
      }
    }
  }

  updateSectionIndicators() {
    // Customer present
    if (this.formData.customerPresent !== null) {
      this.markSectionComplete('customer-present')
    }
    
    // Repair status
    if (this.formData.repairStatus !== null) {
      this.markSectionComplete('repair-status')
    }
    
    // Comments (opcional pero mostramos si está completo)
    if (this.formData.comments) {
      this.markSectionComplete('comments')
    }
  }

  markSectionComplete(sectionName) {
    const section = this.sectionTargets.find(s => s.dataset.sectionName === sectionName)
    if (!section) return
    
    const indicator = section.querySelector('.completion-indicator')
    if (indicator) {
      indicator.textContent = '✓'
      indicator.classList.add('text-green-500')
    }
  }

  submit(e) {
    e.preventDefault()
    
    // Validar firma si cliente presente
    if (this.formData.customerPresent === true) {
      if (!this.validateSignature()) {
        alert('Por favor, obtén la firma del cliente')
        return
      }
      
      // Guardar firma como data URL
      const canvas = this.signatureCanvasTarget
      this.signatureInputTarget.value = canvas.toDataURL('image/png')
    }
    
    // Mostrar loading
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTextTarget.classList.add('hidden')
      this.submitBtnLoaderTarget.classList.remove('hidden')
    }
    
    // Submit form
    const form = this.formTarget
    const formData = new FormData(form)
    
    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      }
    })
    .then(response => {
      if (response.ok) {
        return response.text()
      }
      throw new Error('Network response was not ok')
    })
    .then(html => {
      // Turbo procesará automáticamente el stream (notificación + actualización del ticket)
      Turbo.renderStreamMessage(html)
      
      // Cerrar el formulario inline
      const resolutionController = this.element.closest('[data-controller~="ticket-resolution"]')
      if (resolutionController) {
        const controller = this.application.getControllerForElementAndIdentifier(resolutionController, 'ticket-resolution')
        controller?.close()
      }
      
      // Emitir evento de resolución completada
      window.dispatchEvent(new CustomEvent('ticket-resolution-complete', {
        detail: { ticketId: this.ticketIdValue }
      }))
      
      // Recargar después de 3 segundos para dar tiempo a ver la notificación
      setTimeout(() => window.location.reload(), 3000)
    })
    .catch(error => {
      console.error('Error al resolver ticket:', error)
      alert(`Error: ${error.message}`)
      
      // Restaurar botón
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTextTarget.classList.remove('hidden')
        this.submitBtnLoaderTarget.classList.add('hidden')
      }
    })
  }

  validateSignature() {
    if (!this.hasSignature) return false
    
    const canvas = this.signatureCanvasTarget
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    
    // Verificar que no todos los píxeles sean transparentes/blancos
    const data = imageData.data
    for (let i = 0; i < data.length; i += 4) {
      // Si encontramos algún píxel no blanco/transparente, la firma es válida
      if (data[i + 3] !== 0) {  // Alpha channel
        return true
      }
    }
    
    return false
  }
}
